import { action }         from 'mobx';
import { comparer }       from 'mobx';
import { makeObservable } from 'mobx';
import { observable }     from 'mobx';
import { reaction }       from 'mobx';
import { runInAction }    from 'mobx';
import UserAccountModel   from '@models/account/UserAccountModel';
import ApiCollection      from '@widesk-core/models/ApiCollection';
import appStore           from '@/stores/appStore';

export type AccessKey = 'account' | 'billing' | 'invitations';

const ACCESS_BY_ROLE: Record<Account_UserAccountTypeReference, AccessKey[]> = {
	administrator: ['account', 'billing', 'invitations'],
	editor: [],
	manager: ['account', 'billing'],
};

// Un rôle que le front ne connaît pas n'ouvre rien : mieux vaut masquer un écran auquel l'utilisateur
// avait droit que lui en promettre un que l'API lui refusera.
const accessOfRole = (role: string) => ACCESS_BY_ROLE[role as Account_UserAccountTypeReference] ?? [];

export const roleCan = (role: string | null | undefined, key: AccessKey) => !!role && accessOfRole(role).includes(key);

export class AccessStore {
	@observable
	private _role: Account_UserAccountTypeReference | null = null;

	// Tant que le rôle n'est pas connu, on ne sait rien : les écrans doivent patienter plutôt que
	// d'afficher quoi que ce soit.
	@observable
	private _isLoaded = false;

	private readonly stopWatchingAccount: () => void;

	constructor() {
		makeObservable(this);

		this.stopWatchingAccount = reaction(
			() => [appStore.user.urn, appStore.accountUrn] as const,
			([userUrn, accountUrn]) => this.loadRole(userUrn, accountUrn),
			{ equals: comparer.structural, fireImmediately: true },
		);
	}

	public dispose() {
		this.stopWatchingAccount();
	}

	@action
	private async loadRole(userUrn: Urn | '', accountUrn: Urn | '') {
		this._isLoaded = false;

		if (!userUrn || !accountUrn) {
			this._role = null;
			return;
		}

		// Le rôle déjà connu est conservé le temps de la vérification : l'effacer ferait clignoter
		// les entrées de menu qui en dépendent à chaque changement de compte. Les écrans, eux,
		// attendent `isLoaded` avant de décider quoi que ce soit.

		const collection = new ApiCollection(UserAccountModel);

		try {
			await collection
				.setRequiredFilter('user', userUrn)
				.setRequiredFilter('account', accountUrn)
				.list();

			await collection.whenIsLoaded(m => m.userAccountType);

			const reference = collection.first()?.userAccountType.reference;

			runInAction(() => {
				this._role = reference || null;
				this._isLoaded = true;
			});
		} catch (e) {
			// Vérification impossible : on s'en tient au dernier rôle connu, et à rien du tout si
			// c'est la première.
			runInAction(() => this._isLoaded = true);
		}
	}

	public get isLoaded() {
		return this._isLoaded;
	}

	public get role() {
		return this._role;
	}

	public can(key: AccessKey) {
		return roleCan(this._role, key);
	}
}

export default new AccessStore();
